/**
 * THE PLATFORM SEAM -- one client, two doors.
 *
 * The client builds for two targets from one source tree: the web build
 * (play.reavers.io, Vite mode `production`) and the Discord Activity build
 * (Vite mode `activity`, .env.activity, output dist-activity/). Exactly five
 * things differ between them, and all five live here:
 *
 *   1. where origins point     -> apiBase(), wsBase(), and media (rehostMedia)
 *   2. how identity arrives    -> enterIdentity()
 *   3. how external links open -> openExternal(), wireExternalLinks()
 *   4. what launched this page -> launchContext()
 *   5. how a join is offered   -> offerJoin()
 *
 * INVARIANT: this directory is the ONLY reader of the build mode and of the
 * origin/Discord env values (VITE_API_BASE, VITE_WS_PROXY_URL, VITE_ACTIVITY_*,
 * VITE_DISCORD_*, VITE_STORE_BASE). platform-seam-is-the-only-mode-reader.test.js
 * fails on a read anywhere else. There is deliberately no exported `isActivity`:
 * a behaviour that is not one of the three above must not branch on platform.
 *
 * The one reader outside this directory is index.html's inline
 * `%VITE_API_BASE%` tag, which publishes window.__REAVERS_API_BASE__ for the
 * classic scripts (a classic script cannot parse `import.meta`). It reads the
 * same env value apiBase() returns, so the two cannot disagree within a build.
 *
 * Under Node (the test suites import product modules directly) import.meta.env
 * is undefined; every read below tolerates that and answers as the web build
 * with empty origins, which is what those suites have always seen.
 */

import { joinLink, readWebLaunch } from './web-launch.js';

// The build target. vite.config.js defines __REAVERS_PLATFORM__ as a string
// literal, so ACTIVITY folds to a constant and the activity-only branches (and
// the Discord SDK chunk they import) are removed from the web bundle. Reading
// `import.meta.env.MODE` instead does NOT fold here: the Node-safe guard turns
// it into an object expression Rollup cannot evaluate.
// eslint-disable-next-line no-undef
const PLATFORM = typeof __REAVERS_PLATFORM__ === 'string' ? __REAVERS_PLATFORM__ : 'web';
const ACTIVITY = PLATFORM === 'activity';
// The mock launch mode (Vite mode `activity-mock`, dev server only): the
// Activity client with a mock Discord SDK. Also a defined literal, so the mock
// module folds away in every build -- and that mode refuses to build at all.
// eslint-disable-next-line no-undef
const DISCORD_MOCK = typeof __REAVERS_DISCORD_MOCK__ === 'boolean' ? __REAVERS_DISCORD_MOCK__ : false;

// Origin values (not branch conditions), so they need not fold.
const ENV = (typeof import.meta !== 'undefined' && import.meta.env) || {};

const trimSlash = (s) => String(s || '').replace(/\/+$/, '');

// The Activity's mapped-path prefix. Empty unless the phase 0 spike shows
// Discord needs its `/.proxy` prefix on mapped paths; .env.activity derives
// every mapped path from this one value.
const ACTIVITY_PATH_PREFIX = trimSlash(ENV.VITE_ACTIVITY_PATH_PREFIX);

/**
 * The control-plane API origin, prepended to `/api/...` paths.
 * Web: the absolute gameAPI origin. Activity: the URL-mapped `/api` prefix
 * (Discord strips the prefix, so `/api` + `/api/worlds` reaches gameAPI's
 * `/api/worlds`). Local dev: '' so the Vite dev proxy answers.
 */
export function apiBase() {
    return trimSlash(ENV.VITE_API_BASE);
}

/**
 * The socket relay base; GameServerConnection appends `/{world_id}`.
 * Web: the absolute relay URL from .env.production. Activity: built from
 * location, because the Activity is served from Discord's proxy host and the
 * relay is URL-mapped there (`wss://<host>/ws`). '' means "not configured",
 * which GameServerConnection refuses loudly.
 */
export function wsBase() {
    if (ACTIVITY) {
        const loc = typeof window !== 'undefined' ? window.location : null;
        if (!loc || !loc.host) return '';
        // wss inside Discord (always https); ws only for the mock mode's plain-http dev server.
        const scheme = loc.protocol === 'http:' ? 'ws' : 'wss';
        return `${scheme}://${loc.host}${ACTIVITY_PATH_PREFIX}${trimSlash(ENV.VITE_ACTIVITY_WS_PATH || '/ws')}`;
    }
    return trimSlash(ENV.VITE_WS_PROXY_URL);
}

let discordSdkPromise = null;

// Whether Discord lets this page run SDK commands as the player (set by the
// sign-in's authenticate step). offerJoin reads it; identity does not.
let discordAuth = { authenticated: false, reason: 'no Discord sign-in has run in this page' };

/** The one Discord SDK instance (Activity build only). */
function discordSdk() {
    if (!discordSdkPromise) {
        discordSdkPromise = DISCORD_MOCK
            ? import('./discord-activity-mock.js').then((m) => m.createMockActivitySdk({
                clientId: ENV.VITE_DISCORD_CLIENT_ID,
                pathPrefix: ACTIVITY_PATH_PREFIX,
                search: window.location.search,
            }))
            : import('./discord-activity.js').then((m) => m.createActivitySdk({
                clientId: ENV.VITE_DISCORD_CLIENT_ID,
                pathPrefix: ACTIVITY_PATH_PREFIX,
            }));
    }
    return discordSdkPromise;
}

/**
 * Bring the platform up before anything touches Firebase Auth. Activity: the
 * Firebase Auth endpoints are URL-mapped (patchUrlMappings) and the SDK is
 * constructed -- a restored session refreshes its token through those
 * endpoints, so this must precede the first firebase.auth() use. Web: nothing.
 */
export async function bootPlatform() {
    if (ACTIVITY) {
        await discordSdk();
        // Published for the import-free classic scripts (gameserver-connection.js
        // cannot import this module); the web build publishes nothing.
        window.__REAVERS_REHOST_MEDIA__ = rehostMedia;
    }
}

// Media (narration voice, images) is served from GCS by absolute URL. Inside
// the Activity only URL-mapped hosts are reachable, so the Portal maps
// `/media` -> `storage.googleapis.com` and every such URL is rewritten onto it.
const GCS_ORIGIN = 'https://storage.googleapis.com/';

/**
 * Rewrite every absolute GCS media URL in a server payload onto the Activity's
 * mapped `/media` prefix, keeping the bucket, path and any signature query.
 * The result is ABSOLUTE on the Activity's own origin: consumers that treat a
 * relative URL as a dev-proxy path (gameserver-connection's resolveAudioUrl
 * prefixes `/gandalf`) pass an https:// URL through untouched.
 * Walks objects and arrays; any other value passes through. Called on every
 * GameServer frame and every world-API response. Web: the value, untouched.
 */
export function rehostMedia(value) {
    if (!ACTIVITY) return value;
    return rehostWalk(value);
}

function rehostWalk(value) {
    if (typeof value === 'string') {
        return value.startsWith(GCS_ORIGIN)
            ? `${window.location.origin}${ACTIVITY_PATH_PREFIX}/media/${value.slice(GCS_ORIGIN.length)}`
            : value;
    }
    if (Array.isArray(value)) return value.map(rehostWalk);
    if (value && typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value)) out[k] = rehostWalk(v);
        return out;
    }
    return value;
}

/**
 * How identity arrives. Called on every init, whether or not a session was
 * restored.
 *
 * Web: a restored session is the player's -- keep it (true). With none, navigate
 * to the login page, preserving the query string (isolated-stack overrides such
 * as ?ws_port= survive it), and return false because the page is leaving;
 * `mayNavigate: false` (a fast-path or test page awaiting in-place auth) stays
 * put and returns true.
 *
 * Activity: identity comes from DISCORD ON EVERY LAUNCH, never from a restored
 * session. A session left in the tab may be another Discord user's (an account
 * switch), and it carries no SDK authentication, so it is signed out and the
 * handshake runs: it signs the player in in place, authenticates the SDK as
 * them, and resolves true. It never navigates, so `mayNavigate` does not apply.
 * A first-time player passes the first-time screen first; when they are
 * refused, or choose to link an existing reavers.io account first, this
 * resolves false (no session, init stops) and the screen says what happens
 * next. Any other failure throws, loudly.
 *
 * @param {object} firebaseCompat the compat `firebase` global
 * @param {{restored?: boolean, mayNavigate?: boolean}} [state]
 * @returns {Promise<boolean>} true when a session now exists in this page
 */
export async function enterIdentity(firebaseCompat, { restored = false, mayNavigate = true } = {}) {
    if (ACTIVITY) {
        if (restored) {
            console.log('[platform] identity.discord path=per-launch restored-session=replaced');
            await firebaseCompat.auth().signOut();
        }
        const [{ signInThroughDiscord }, { createFirstTimeScreen }, sdk] = await Promise.all([
            import('./discord-identity.js'),
            import('./discord-first-time.js'),
            discordSdk(),
        ]);
        const firstTime = createFirstTimeScreen({ document: window.document, openExternal });
        const result = await signInThroughDiscord({
            sdk,
            clientId: ENV.VITE_DISCORD_CLIENT_ID,
            storeBase: `${ACTIVITY_PATH_PREFIX}${trimSlash(ENV.VITE_STORE_BASE || '/store')}`,
            fetchImpl: (...args) => window.fetch(...args),
            auth: firebaseCompat.auth(),
            persistence: firebaseCompat.auth.Auth.Persistence,
            firstTime,
        });
        if (result.signedIn) firstTime.close();
        if (result.discord) discordAuth = result.discord;
        return result.signedIn;
    }
    if (restored || !mayNavigate) return true;
    window.location.href = '/login.html' + window.location.search;
    return false;
}

/**
 * The platform launch this page was opened by, for WMS `/api/worlds/launch`.
 * Activity: what the SDK says of the launch (discord-launch.js readLaunch).
 * Web: a visit by a shared link (`?join=<token>`, web-launch.js readWebLaunch);
 * null for a plain visit, which is not a launch.
 */
export async function launchContext() {
    if (ACTIVITY) {
        const [{ readLaunch }, sdk] = await Promise.all([import('./discord-launch.js'), discordSdk()]);
        const ctx = readLaunch(sdk);
        console.log(`[platform] launch.context instance=${ctx.instance_id} `
            + `custom_id=${JSON.stringify(sdk.customId)} referrer_id=${JSON.stringify(sdk.referrerId)} `
            + `platform=${ctx.platform}`);
        return ctx;
    }
    const ctx = typeof window !== 'undefined' ? readWebLaunch(window.location.search) : null;
    if (ctx) console.log('[platform] launch.context platform=web join_token=present');
    return ctx;
}

/**
 * Offer a join token to friends. Activity: Discord's shareLink, whose
 * `custom_id` comes back to the friend's launch; resolves what the player did
 * with the share window (discord-launch.js shareOutcome). Web: a link to this
 * client that names the token (web-launch.js joinLink), copied to the clipboard
 * when the browser allows it; `link` carries it for the caller to show.
 *
 * @returns {Promise<{shared: boolean, sent: boolean, copied: boolean, link?: string}>}
 */
export async function offerJoin(token, message) {
    if (ACTIVITY) {
        if (!discordAuth.authenticated) {
            throw new Error(`Sharing is unavailable in this session: ${discordAuth.reason}`);
        }
        const [{ shareOutcome }, sdk] = await Promise.all([import('./discord-launch.js'), discordSdk()]);
        const outcome = shareOutcome(await sdk.commands.shareLink({ message, custom_id: token }));
        console.log(`[platform] share.offer path=${outcome.shared ? 'shared' : 'closed'} `
            + `sent=${outcome.sent} copied=${outcome.copied}`);
        return outcome;
    }
    const link = joinLink(window.location.origin, token);
    let copied = false;
    try {
        await window.navigator.clipboard.writeText(link);
        copied = true;
    } catch (err) {
        // No clipboard (an insecure origin, a denied permission): the link is shown.
        console.log(`[platform] share.offer clipboard unavailable: ${err && err.message ? err.message : err}`);
    }
    console.log(`[platform] share.offer path=link copied=${copied}`);
    return { shared: true, sent: false, copied, link };
}

/**
 * Open a URL outside the client.
 * Web: a new tab -- leaving the page would drop the socket and the player's
 * turn. Activity: Discord's openExternalLink, the only exit an Activity has
 * (and the only purchase path on mobile).
 */
export async function openExternal(url) {
    if (ACTIVITY) {
        const sdk = await discordSdk();
        return sdk.commands.openExternalLink({ url });
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    return undefined;
}

/**
 * Route every `<a data-external>` click through openExternal(). The anchors
 * keep their href so the markup still describes where they go.
 */
export function wireExternalLinks(root) {
    root.addEventListener('click', (event) => {
        const link = event.target && event.target.closest && event.target.closest('a[data-external]');
        if (!link) return;
        event.preventDefault();
        openExternal(link.href).catch((err) => {
            console.error('[platform] openExternal failed:', repr(err), link.href);
        });
    });
}

function repr(err) {
    return err && err.stack ? err.stack : String(err);
}
